import { Controller } from '@hotwired/stimulus'

/**
 * Gallery Manager Controller
 *
 * Handles gallery image management including:
 * - Drag & drop upload with progress
 * - Image sorting via drag & drop
 * - Bulk selection and actions
 * - Search and filtering
 * - Edit modal with focal point picker
 * - Real-time status updates
 */
export default class extends Controller {
  static targets = [
    'grid', 'imageCard', 'uploadInput', 'uploadProgress', 'uploadDropZone',
    'searchInput', 'categoryFilter', 'statusFilter', 'statusLabel', 'usageFilter',
    'sourceFilter', 'sourceLabel', 'publicationFilter', 'publicationLabel',
    'activeFilters', 'filterTags',
    'bulkActions', 'bulkCheckbox', 'selectAllCheckbox', 'selectionCount',
    'editModal', 'focalPointCanvas', 'focalPointMarker', 'editForm',
    'processingOverlay'
  ]

  static values = {
    websiteId: String,
    uploadUrl: String,
    reorderUrl: String,
    bulkActionUrl: String,
    csrfToken: String
  }

  connect() {
    this.selectedIds = new Set()
    this.initDragAndDrop()
    this.initSortable()

    // Initialize filter state
    this.filters = {
      category: '',
      status: '',
      usage: '',
      source: '',
      publication: ''
    }

    // Get CSRF token
    this.csrfTokenValue = document.querySelector('meta[name="csrf-token"]')?.content
  }

  // ==================== DRAG & DROP UPLOAD ====================

  initDragAndDrop() {
    if (!this.hasUploadDropZoneTarget) return

    const zone = this.uploadDropZoneTarget

    // Prevent default drag behaviors on the window
    ;['dragenter', 'dragover', 'dragleave', 'drop'].forEach((eventName) => {
      zone.addEventListener(eventName, this.preventDefaults.bind(this), false)
    })

    // Highlight drop zone when dragging over
    ;['dragenter', 'dragover'].forEach((eventName) => {
      zone.addEventListener(eventName, () => this.highlightDropZone(true), false)
    });
    ['dragleave', 'drop'].forEach((eventName) => {
      zone.addEventListener(eventName, () => this.highlightDropZone(false), false)
    })

    // Handle dropped files
    zone.addEventListener('drop', this.handleDrop.bind(this), false)
  }

  preventDefaults(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  highlightDropZone(active) {
    if (this.hasUploadDropZoneTarget) {
      this.uploadDropZoneTarget.classList.toggle('border-primary-500', active)
      this.uploadDropZoneTarget.classList.toggle('bg-primary-50', active)
    }
  }

  handleDrop(e) {
    const files = [...e.dataTransfer.files].filter((file) => file.type.startsWith('image/'))

    if (files.length === 0) {
      this.showToast('Please drop image files only', 'error')
      return
    }

    this.uploadFiles(files)
  }

  // Triggered by file input change
  handleFileSelect(e) {
    const files = [...e.target.files]
    if (files.length > 0) {
      this.uploadFiles(files)
    }
    e.target.value = '' // Reset input
  }

  async uploadFiles(files) {
    const maxSize = 10 * 1024 * 1024 // 10MB

    for (const file of files) {
      if (file.size > maxSize) {
        this.showToast(`${file.name} is too large (max 10MB)`, 'error')
        continue
      }

      await this.uploadSingleFile(file)
    }
  }

  async uploadSingleFile(file) {
    const formData = new FormData()
    formData.append('website_gallery_image[image]', file)
    formData.append('website_gallery_image[category]', 'general')

    // Show progress indicator
    const progressId = this.showUploadProgress(file.name)

    try {
      const response = await fetch(this.uploadUrlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.csrfTokenValue,
          'Accept': 'application/json'
        },
        body: formData
      })

      const data = await response.json()

      if (response.ok) {
        this.showToast(`${file.name} uploaded successfully`, 'success')
        this.appendImageCard(data)
      } else {
        this.showToast(data.errors?.join(', ') || 'Upload failed', 'error')
      }
    } catch (error) {
      console.error('Upload error:', error)
      this.showToast(`Upload failed: ${error.message}`, 'error')
    } finally {
      this.hideUploadProgress(progressId)
    }
  }

  showUploadProgress(filename) {
    const id = `progress-${Date.now()}`

    if (this.hasUploadProgressTarget) {
      const progressHtml = `
        <div id="${id}" class="flex items-center gap-2 p-2 bg-surface-raised rounded-lg">
          <div class="animate-spin w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full"></div>
          <span class="text-sm truncate">${filename}</span>
        </div>
      `
      this.uploadProgressTarget.insertAdjacentHTML('beforeend', progressHtml)
    }

    return id
  }

  hideUploadProgress(id) {
    const el = document.getElementById(id)
    if (el) el.remove()
  }

  appendImageCard(imageData) {
    if (!this.hasGridTarget) return

    // Insert a new card at the beginning of the grid
    const cardHtml = this.buildImageCardHtml(imageData)
    this.gridTarget.insertAdjacentHTML('afterbegin', cardHtml)
  }

  buildImageCardHtml(image) {
    const thumbnailUrl = image.thumbnail_url || image.url
    const statusBadge = image.processing_status === 'processing' ?
      '<div class="absolute top-2 right-2 badge badge-warning">Processing...</div>' :
      ''
    const featuredBadge = image.featured ?
      '<div class="absolute top-2 left-2 badge badge-primary">Featured</div>' :
      ''

    return `
      <div class="image-card group relative rounded-lg overflow-hidden border"
           style="border-color: var(--border-muted);"
           data-admin--gallery-manager-target="imageCard"
           data-image-id="${image.id}"
           data-category="${image.category}"
           data-processing-status="${image.processing_status || 'pending'}">

        <input type="checkbox" class="bulk-checkbox absolute top-2 left-2 z-10 w-4 h-4 hidden group-hover:block"
               data-admin--gallery-manager-target="bulkCheckbox"
               data-action="change->admin--gallery-manager#toggleSelect"
               data-image-id="${image.id}">

        <img src="${thumbnailUrl}" alt="${image.alt_text || ''}"
             class="w-full aspect-square object-cover"
             loading="lazy"
             style="object-position: ${image.focal_point_css || '50% 50%'};">

        ${statusBadge}
        ${featuredBadge}

        <div class="absolute bottom-2 left-2">
          <span class="badge badge-dark">${(image.category || 'general').replace(/_/g, ' ')}</span>
        </div>

        <div class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
          <button type="button" class="p-2 rounded-full bg-white/90 hover:bg-white text-gray-700"
                  data-action="click->admin--gallery-manager#openEditModal"
                  data-image-id="${image.id}"
                  title="Edit">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
            </svg>
          </button>
          <button type="button" class="p-2 rounded-full bg-white/90 hover:bg-white text-gray-700"
                  data-action="click->admin--gallery-manager#copyUrl"
                  data-url="${image.url}"
                  title="Copy URL">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"/>
            </svg>
          </button>
          <button type="button" class="p-2 rounded-full bg-white/90 hover:bg-white text-red-600"
                  data-action="click->admin--gallery-manager#deleteImage"
                  data-image-id="${image.id}"
                  title="Delete">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
            </svg>
          </button>
        </div>
      </div>
    `
  }

  // ==================== SORTABLE ====================

  initSortable() {
    if (!this.hasGridTarget) return

    // Make image cards draggable
    this.imageCardTargets.forEach((card) => {
      card.setAttribute('draggable', 'true')
      card.addEventListener('dragstart', this.handleSortStart.bind(this))
      card.addEventListener('dragover', this.handleSortOver.bind(this))
      card.addEventListener('drop', this.handleSortDrop.bind(this))
      card.addEventListener('dragend', this.handleSortEnd.bind(this))
    })
  }

  handleSortStart(e) {
    this.draggedCard = e.target.closest('[data-image-id]')
    if (this.draggedCard) {
      this.draggedCard.classList.add('opacity-50')
      e.dataTransfer.effectAllowed = 'move'
    }
  }

  handleSortOver(e) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'

    const target = e.target.closest('[data-image-id]')
    if (target && target !== this.draggedCard) {
      const rect = target.getBoundingClientRect()
      const midX = rect.left + rect.width / 2

      this.clearSortIndicators()

      if (e.clientX < midX) {
        target.classList.add('ring-2', 'ring-primary-500', 'ring-offset-2')
        this.dropPosition = 'before'
      } else {
        target.classList.add('ring-2', 'ring-primary-500', 'ring-offset-2')
        this.dropPosition = 'after'
      }
      this.dropTarget = target
    }
  }

  handleSortDrop(e) {
    e.preventDefault()

    if (this.dropTarget && this.draggedCard && this.dropTarget !== this.draggedCard) {
      if (this.dropPosition === 'before') {
        this.dropTarget.parentNode.insertBefore(this.draggedCard, this.dropTarget)
      } else {
        this.dropTarget.parentNode.insertBefore(this.draggedCard, this.dropTarget.nextSibling)
      }
      this.saveOrder()
    }

    this.clearSortIndicators()
  }

  handleSortEnd(_e) {
    if (this.draggedCard) {
      this.draggedCard.classList.remove('opacity-50')
    }
    this.clearSortIndicators()
  }

  clearSortIndicators() {
    this.imageCardTargets.forEach((card) => {
      card.classList.remove('ring-2', 'ring-primary-500', 'ring-offset-2')
    })
  }

  async saveOrder() {
    const imageIds = this.imageCardTargets.map((card) => card.dataset.imageId)

    try {
      await fetch(this.reorderUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfTokenValue
        },
        body: JSON.stringify({ image_ids: imageIds })
      })
    } catch (error) {
      console.error('Reorder error:', error)
      this.showToast('Failed to save order', 'error')
    }
  }

  // ==================== BULK SELECTION ====================

  toggleSelect(e) {
    const imageId = e.target.dataset.imageId
    if (e.target.checked) {
      this.selectedIds.add(imageId)
    } else {
      this.selectedIds.delete(imageId)
    }
    this.updateBulkActionsUI()
  }

  selectAll(e) {
    const checked = e.target.checked
    this.bulkCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = checked
      const imageId = checkbox.dataset.imageId
      if (checked) {
        this.selectedIds.add(imageId)
      } else {
        this.selectedIds.delete(imageId)
      }
    })
    this.updateBulkActionsUI()
  }

  updateBulkActionsUI() {
    const count = this.selectedIds.size

    if (this.hasBulkActionsTarget) {
      this.bulkActionsTarget.classList.toggle('hidden', count === 0)
    }

    if (this.hasSelectionCountTarget) {
      this.selectionCountTarget.textContent = `${count} selected`
    }

    // Show/hide checkboxes based on selection mode
    this.bulkCheckboxTargets.forEach((checkbox) => {
      checkbox.classList.toggle('block', count > 0)
      checkbox.classList.toggle('hidden', count === 0)
    })
  }

  clearSelection() {
    this.selectedIds.clear()
    this.bulkCheckboxTargets.forEach((checkbox) => checkbox.checked = false)
    if (this.hasSelectAllCheckboxTarget) {
      this.selectAllCheckboxTarget.checked = false
    }
    this.updateBulkActionsUI()
  }

  async bulkDelete() {
    if (!confirm(`Delete ${this.selectedIds.size} image(s)? This cannot be undone.`)) return
    await this.performBulkAction('delete')
  }

  async bulkFeature() {
    await this.performBulkAction('feature')
  }

  async bulkUnfeature() {
    await this.performBulkAction('unfeature')
  }

  async bulkRecategorize(e) {
    const category = e.target.dataset.category
    await this.performBulkAction('recategorize', { category })
  }

  async bulkReprocess() {
    await this.performBulkAction('reprocess')
  }

  async performBulkAction(action, extraParams = {}) {
    try {
      const response = await fetch(this.bulkActionUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfTokenValue
        },
        body: JSON.stringify({
          bulk_action: action,
          image_ids: [...this.selectedIds],
          ...extraParams
        })
      })

      const data = await response.json()

      if (response.ok) {
        this.showToast(data.message, 'success')

        // Remove deleted cards from DOM
        if (action === 'delete') {
          this.selectedIds.forEach((id) => {
            const card = this.imageCardTargets.find((c) => c.dataset.imageId === id)
            if (card) card.remove()
          })
        }

        this.clearSelection()

        // Refresh if needed for category/feature changes
        if (['feature', 'unfeature', 'recategorize'].includes(action)) {
          location.reload()
        }
      } else {
        this.showToast(data.error || 'Action failed', 'error')
      }
    } catch (error) {
      console.error('Bulk action error:', error)
      this.showToast(`Action failed: ${error.message}`, 'error')
    }
  }

  // ==================== SEARCH & FILTER ====================

  search(_e) {
    // Debounce search input
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => {
      this.filterImages()
      this.updateActiveFiltersUI()
    }, 150)
  }

  filterByCategory(e) {
    const category = e.target.closest('[data-category]')?.dataset.category || ''
    this.filters.category = category

    // Update pill active states
    if (this.hasCategoryFilterTarget) {
      this.categoryFilterTarget.querySelectorAll('.filter-pill').forEach((pill) => {
        pill.classList.toggle('active', pill.dataset.category === category)
      })
    }

    this.filterImages()
    this.updateActiveFiltersUI()
  }

  filterByStatus(e) {
    const status = e.target.closest('[data-status]')?.dataset.status || ''
    this.filters.status = status

    // Update status label
    if (this.hasStatusLabelTarget) {
      const labels = { '': 'Status', 'completed': 'Processed', 'pending': 'Pending', 'failed': 'Failed' }
      this.statusLabelTarget.textContent = labels[status] || 'Status'
    }

    this.filterImages()
    this.updateActiveFiltersUI()
  }

  filterByUsage(e) {
    const usage = e.target.closest('[data-usage]')?.dataset.usage || ''
    this.filters.usage = usage

    // Update toggle active states
    if (this.hasUsageFilterTarget) {
      this.usageFilterTarget.querySelectorAll('.usage-toggle').forEach((toggle) => {
        toggle.classList.toggle('active', toggle.dataset.usage === usage)
      })
    }

    this.filterImages()
    this.updateActiveFiltersUI()
  }

  filterBySource(e) {
    const source = e.target.closest('[data-source]')?.dataset.source || ''
    this.filters.source = source

    // Update toggle active states
    if (this.hasSourceFilterTarget) {
      this.sourceFilterTarget.querySelectorAll('.usage-toggle').forEach((toggle) => {
        toggle.classList.toggle('active', toggle.dataset.source === source)
      })
    }

    this.filterImages()
    this.updateActiveFiltersUI()
  }

  filterByPublication(e) {
    const publication = e.target.closest('[data-publication]')?.dataset.publication || ''
    this.filters.publication = publication

    // Update label
    if (this.hasPublicationLabelTarget) {
      const labels = { '': 'Publication', 'generated': 'Generated', 'published': 'Published', 'replaced': 'Replaced' }
      this.publicationLabelTarget.textContent = labels[publication] || 'Publication'
    }

    this.filterImages()
    this.updateActiveFiltersUI()
  }

  clearFilters() {
    this.filters = { category: '', status: '', usage: '', source: '', publication: '' }

    // Reset search
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ''
    }

    // Reset category pills
    if (this.hasCategoryFilterTarget) {
      this.categoryFilterTarget.querySelectorAll('.filter-pill').forEach((pill) => {
        pill.classList.toggle('active', pill.dataset.category === '')
      })
    }

    // Reset status label
    if (this.hasStatusLabelTarget) {
      this.statusLabelTarget.textContent = 'Status'
    }

    // Reset usage toggles
    if (this.hasUsageFilterTarget) {
      this.usageFilterTarget.querySelectorAll('.usage-toggle').forEach((toggle) => {
        toggle.classList.toggle('active', toggle.dataset.usage === '')
      })
    }

    // Reset source toggles
    if (this.hasSourceFilterTarget) {
      this.sourceFilterTarget.querySelectorAll('.usage-toggle').forEach((toggle) => {
        toggle.classList.toggle('active', toggle.dataset.source === '')
      })
    }

    // Reset publication label
    if (this.hasPublicationLabelTarget) {
      this.publicationLabelTarget.textContent = 'Publication'
    }

    this.filterImages()
    this.updateActiveFiltersUI()
  }

  filterImages() {
    const query = this.hasSearchInputTarget ? this.searchInputTarget.value.toLowerCase() : ''
    const { category, status, usage, source, publication } = this.filters

    let visibleCount = 0

    this.imageCardTargets.forEach((card) => {
      let visible = true

      // Filter by category
      if (category && card.dataset.category !== category) {
        visible = false
      }

      // Filter by status
      if (status && card.dataset.processingStatus !== status) {
        visible = false
      }

      // Filter by usage
      if (usage === 'unused' && card.dataset.usageCount !== '0') {
        visible = false
      } else if (usage === 'used' && card.dataset.usageCount === '0') {
        visible = false
      }

      // Filter by source (AI generated vs uploaded)
      if (source === 'ai' && card.dataset.aiGenerated !== 'true') {
        visible = false
      } else if (source === 'uploaded' && card.dataset.aiGenerated === 'true') {
        visible = false
      }

      // Filter by publication status
      if (publication && card.dataset.publicationStatus !== publication) {
        visible = false
      }

      // Filter by search query (checks title stored in data attribute)
      if (query) {
        const title = (card.dataset.title || '').toLowerCase()
        const categoryText = (card.dataset.category || '').toLowerCase()
        if (!title.includes(query) && !categoryText.includes(query)) {
          visible = false
        }
      }

      card.classList.toggle('hidden', !visible)
      if (visible) visibleCount++
    })

    return visibleCount
  }

  updateActiveFiltersUI() {
    const hasFilters = this.filters.category || this.filters.status || this.filters.usage ||
                       this.filters.source || this.filters.publication ||
                       (this.hasSearchInputTarget && this.searchInputTarget.value)

    // Show/hide active filters row
    if (this.hasActiveFiltersTarget) {
      this.activeFiltersTarget.classList.toggle('hidden', !hasFilters)
    }

    // Build filter tags
    if (this.hasFilterTagsTarget) {
      const tags = []

      if (this.filters.category) {
        tags.push(this.buildFilterTag('Category', this.filters.category.replace(/_/g, ' '), 'category'))
      }
      if (this.filters.status) {
        const statusLabels = { 'completed': 'Processed', 'pending': 'Pending', 'failed': 'Failed' }
        tags.push(this.buildFilterTag('Status', statusLabels[this.filters.status], 'status'))
      }
      if (this.filters.usage) {
        tags.push(this.buildFilterTag('Usage', this.filters.usage === 'used' ? 'Used' : 'Unused', 'usage'))
      }
      if (this.filters.source) {
        tags.push(this.buildFilterTag('Source', this.filters.source === 'ai' ? 'AI Generated' : 'Uploaded', 'source'))
      }
      if (this.filters.publication) {
        const pubLabels = { 'generated': 'Generated', 'published': 'Published', 'replaced': 'Replaced' }
        tags.push(this.buildFilterTag('Publication', pubLabels[this.filters.publication], 'publication'))
      }
      if (this.hasSearchInputTarget && this.searchInputTarget.value) {
        tags.push(this.buildFilterTag('Search', `"${this.searchInputTarget.value}"`, 'search'))
      }

      this.filterTagsTarget.innerHTML = tags.join('')
    }
  }

  buildFilterTag(label, value, type) {
    return `
      <span class="filter-tag">
        ${value}
        <button type="button" data-action="click->admin--gallery-manager#removeFilter" data-filter-type="${type}">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </span>
    `
  }

  removeFilter(e) {
    const type = e.target.closest('[data-filter-type]')?.dataset.filterType

    switch (type) {
      case 'category':
        this.filters.category = ''
        if (this.hasCategoryFilterTarget) {
          this.categoryFilterTarget.querySelectorAll('.filter-pill').forEach((pill) => {
            pill.classList.toggle('active', pill.dataset.category === '')
          })
        }
        break
      case 'status':
        this.filters.status = ''
        if (this.hasStatusLabelTarget) {
          this.statusLabelTarget.textContent = 'Status'
        }
        break
      case 'usage':
        this.filters.usage = ''
        if (this.hasUsageFilterTarget) {
          this.usageFilterTarget.querySelectorAll('.usage-toggle').forEach((toggle) => {
            toggle.classList.toggle('active', toggle.dataset.usage === '')
          })
        }
        break
      case 'source':
        this.filters.source = ''
        if (this.hasSourceFilterTarget) {
          this.sourceFilterTarget.querySelectorAll('.usage-toggle').forEach((toggle) => {
            toggle.classList.toggle('active', toggle.dataset.source === '')
          })
        }
        break
      case 'publication':
        this.filters.publication = ''
        if (this.hasPublicationLabelTarget) {
          this.publicationLabelTarget.textContent = 'Publication'
        }
        break
      case 'search':
        if (this.hasSearchInputTarget) {
          this.searchInputTarget.value = ''
        }
        break
    }

    this.filterImages()
    this.updateActiveFiltersUI()
  }

  // ==================== EDIT MODAL ====================

  async openEditModal(e) {
    const imageId = e.target.closest('[data-image-id]').dataset.imageId

    try {
      const response = await fetch(`/admin/websites/${this.websiteIdValue}/gallery/${imageId}.json`)
      const image = await response.json()

      this.showEditModal(image)
    } catch (error) {
      console.error('Failed to load image:', error)
      this.showToast('Failed to load image details', 'error')
    }
  }

  showEditModal(image) {
    if (!this.hasEditModalTarget) return

    // Populate form fields
    const modal = this.editModalTarget
    modal.querySelector('[name="image_id"]')?.setAttribute('value', image.id)
    modal.querySelector('[name="website_gallery_image[title]"]')?.setAttribute('value', image.title || '')
    modal.querySelector('[name="website_gallery_image[description]"]')?.setAttribute('value', image.description || '')
    modal.querySelector('[name="website_gallery_image[alt_text]"]')?.setAttribute('value', image.alt_text || '')

    const categorySelect = modal.querySelector('[name="website_gallery_image[category]"]')
    if (categorySelect) categorySelect.value = image.category

    const featuredCheckbox = modal.querySelector('[name="website_gallery_image[featured]"]')
    if (featuredCheckbox) featuredCheckbox.checked = image.featured

    // Set focal point inputs
    const focalXInput = modal.querySelector('[name="website_gallery_image[focal_point_x]"]')
    const focalYInput = modal.querySelector('[name="website_gallery_image[focal_point_y]"]')
    if (focalXInput) focalXInput.value = image.focal_point_x || 0.5
    if (focalYInput) focalYInput.value = image.focal_point_y || 0.5

    // Load image preview with focal point
    const preview = modal.querySelector('[data-focal-point-preview]')
    if (preview) {
      preview.src = image.url
      this.initFocalPointPicker(preview, image.focal_point_x, image.focal_point_y)
    }

    // Populate usages
    const usagesContainer = modal.querySelector('[data-usages-container]')
    if (usagesContainer && image.usages) {
      if (image.usages.length > 0) {
        usagesContainer.innerHTML = `
          <h4 class="text-sm font-medium mb-2">Used in ${image.usages.length} location(s):</h4>
          <ul class="text-sm space-y-1">
            ${image.usages.map((u) => `
              <li>
                <a href="${u.url}" class="text-primary-600 hover:underline">
                  ${u.label}
                </a>
                ${u.context ? `<span class="text-gray-500">(${u.context})</span>` : ''}
              </li>
            `).join('')}
          </ul>
        `
      } else {
        usagesContainer.innerHTML = '<p class="text-sm text-gray-500">Not used anywhere yet</p>'
      }
    }

    // Store current image id for save
    this.currentEditImageId = image.id

    // Show modal
    modal.classList.add('is-open')
    modal.querySelector('.modal-backdrop')?.classList.add('is-open')
    modal.querySelector('.modal')?.classList.add('is-open')
  }

  closeEditModal() {
    if (!this.hasEditModalTarget) return

    const modal = this.editModalTarget
    modal.classList.remove('is-open')
    modal.querySelector('.modal-backdrop')?.classList.remove('is-open')
    modal.querySelector('.modal')?.classList.remove('is-open')
    this.currentEditImageId = null
  }

  initFocalPointPicker(preview, x = 0.5, y = 0.5) {
    // Position marker
    if (this.hasFocalPointMarkerTarget) {
      this.focalPointMarkerTarget.style.left = `${x * 100}%`
      this.focalPointMarkerTarget.style.top = `${y * 100}%`
    }

    // Add click handler to preview container
    const container = preview.parentElement
    container.addEventListener('click', (e) => {
      const rect = preview.getBoundingClientRect()
      const newX = (e.clientX - rect.left) / rect.width
      const newY = (e.clientY - rect.top) / rect.height

      // Update marker position
      if (this.hasFocalPointMarkerTarget) {
        this.focalPointMarkerTarget.style.left = `${newX * 100}%`
        this.focalPointMarkerTarget.style.top = `${newY * 100}%`
      }

      // Update hidden inputs
      const modal = this.editModalTarget
      const focalXInput = modal.querySelector('[name="website_gallery_image[focal_point_x]"]')
      const focalYInput = modal.querySelector('[name="website_gallery_image[focal_point_y]"]')
      if (focalXInput) focalXInput.value = newX.toFixed(2)
      if (focalYInput) focalYInput.value = newY.toFixed(2)
    })
  }

  async saveEditModal(e) {
    e.preventDefault()

    if (!this.currentEditImageId) return

    const form = e.target
    const formData = new FormData(form)

    try {
      const response = await fetch(`/admin/websites/${this.websiteIdValue}/gallery/${this.currentEditImageId}`, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': this.csrfTokenValue,
          'Accept': 'application/json'
        },
        body: formData
      })

      const data = await response.json()

      if (response.ok) {
        this.showToast('Image updated', 'success')
        this.closeEditModal()

        // Update the card in the grid
        const card = this.imageCardTargets.find((c) => c.dataset.imageId === this.currentEditImageId)
        if (card) {
          card.dataset.category = data.category
          card.dataset.title = data.title || ''

          // Update image object-position for focal point
          const img = card.querySelector('img')
          if (img && data.focal_point_css) {
            img.style.objectPosition = data.focal_point_css
          }

          // Update category badge
          const categoryBadge = card.querySelector('.badge-dark')
          if (categoryBadge) {
            categoryBadge.textContent = data.category.replace(/_/g, ' ')
          }

          // Update featured badge
          if (data.featured) {
            if (!card.querySelector('.badge-primary')) {
              card.insertAdjacentHTML(
                'afterbegin',
                '<div class="absolute top-2 left-2 badge badge-primary">Featured</div>'
              )
            }
          } else {
            card.querySelector('.badge-primary')?.remove()
          }
        }
      } else {
        this.showToast(data.errors?.join(', ') || 'Update failed', 'error')
      }
    } catch (error) {
      console.error('Save error:', error)
      this.showToast(`Save failed: ${error.message}`, 'error')
    }
  }

  // ==================== SINGLE ACTIONS ====================

  async deleteImage(e) {
    const imageId = e.target.closest('[data-image-id]').dataset.imageId
    if (!confirm('Delete this image?')) return

    try {
      const response = await fetch(`/admin/websites/${this.websiteIdValue}/gallery/${imageId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': this.csrfTokenValue,
          'Accept': 'application/json'
        }
      })

      const data = await response.json()

      if (response.ok) {
        // Remove card from DOM
        const card = this.imageCardTargets.find((c) => c.dataset.imageId === imageId)
        if (card) card.remove()

        this.showToast(data.warning || 'Image deleted', data.warning ? 'warning' : 'success')
      } else {
        this.showToast('Delete failed', 'error')
      }
    } catch (error) {
      console.error('Delete error:', error)
      this.showToast(`Delete failed: ${error.message}`, 'error')
    }
  }

  copyUrl(e) {
    const url = e.target.closest('[data-url]')?.dataset.url

    if (url) {
      navigator.clipboard.writeText(url).then(() => {
        this.showToast('URL copied to clipboard', 'success')
      }).catch((err) => {
        console.error('Copy failed:', err)
        this.showToast('Failed to copy URL', 'error')
      })
    }
  }

  async reprocessImage(e) {
    const imageId = e.target.closest('[data-image-id]').dataset.imageId

    try {
      const response = await fetch(`/admin/websites/${this.websiteIdValue}/gallery/${imageId}/reprocess`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.csrfTokenValue,
          'Accept': 'application/json'
        }
      })

      const data = await response.json()

      if (response.ok) {
        this.showToast('Image queued for reprocessing', 'success')

        // Update card status
        const card = this.imageCardTargets.find((c) => c.dataset.imageId === imageId)
        if (card) {
          card.dataset.processingStatus = 'processing'
          // Add processing badge if not present
          if (!card.querySelector('.badge-warning')) {
            const img = card.querySelector('img')
            img?.insertAdjacentHTML(
              'afterend',
              '<div class="absolute top-2 right-2 badge badge-warning">Processing...</div>'
            )
          }
        }
      } else {
        this.showToast(data.error || 'Reprocess failed', 'error')
      }
    } catch (error) {
      console.error('Reprocess error:', error)
      this.showToast(`Reprocess failed: ${error.message}`, 'error')
    }
  }

  // ==================== TOAST NOTIFICATIONS ====================

  showToast(message, type = 'info') {
    // Try to use mdtoast if available, fallback to simple notification
    if (typeof mdtoast !== 'undefined') {
      mdtoast(message, {
        type: type,
        position: 'bottom right',
        duration: 3000
      })
    } else {
      // Simple fallback toast
      const toast = document.createElement('div')
      toast.className = `fixed bottom-4 right-4 px-4 py-2 rounded-lg text-white z-50 ${
        type === 'success' ? 'bg-green-600' :
          type === 'error' ? 'bg-red-600' :
            type === 'warning' ? 'bg-yellow-600' :
              'bg-gray-700'
      }`
      toast.textContent = message
      document.body.appendChild(toast)

      setTimeout(() => toast.remove(), 3000)
    }
  }
}
